import { getRangeMinMax } from '../../machine/reducers/utils/range/get-range-min-max';
import { type SelectionState } from '../../machine/state/types';
import { sumSelectedRowSpan } from '../../machine/utils/sum-selected-row-span';
import { createEffect } from '../utils/create-effect';

type HeaderCheckboxState = 'checked' | 'unchecked' | 'indeterminate';

function getHeaderCheckboxState(
  state: SelectionState,
  totalEntryCount: number
): HeaderCheckboxState {
  if (state.mode !== 'row' || state.selections.length === 0 || totalEntryCount === 0) {
    return 'unchecked';
  }

  const selectedCount = sumSelectedRowSpan(state);

  if (selectedCount === totalEntryCount) {
    return 'checked';
  }

  return 'indeterminate';
}

export interface CheckboxEffectDeps {
  /**
   * Provides visible entry IDs for row index resolution and select-all.
   * getVisibleEntryIds() returns all visible entry IDs in order.
   * Its length is the total entry count (required for header checkbox state).
   * Index i resolves to the entry ID at row index i.
   */
  databaseAdapter: { getVisibleEntryIds(): string[] };

  /**
   * Notifies a specific row that its selection state has changed.
   * The row checkbox component subscribes to this via the ping registry.
   */
  notifyRowSelected: (entryId: string, selected: boolean) => void;

  /**
   * Notifies the header checkbox that the aggregate selection state has changed.
   */
  notifyHeaderCheckboxState: (state: HeaderCheckboxState) => void;
}

/**
 * Creates an effect that synchronizes checkbox state with the selection machine
 * via the ping registry.
 *
 * Instead of doing direct DOM mutations (which conflict with Atlaskit Checkbox's
 * internal state management), this effect notifies the ping registry when
 * selection state changes. The React checkbox components subscribe to these
 * pings and update their own state via `useState`, triggering clean React re-renders.
 *
 * This effect works in conjunction with the render-time reads in `SelectionsEntryCheckbox`
 * and `SelectionsHeaderCheckbox` which handle the initial state when components
 * mount (e.g., due to virtualization scrolling rows into view).
 */
export const createCheckboxEffect = createEffect((deps: CheckboxEffectDeps) => {
  let previouslySelectedEntryIds = new Set<string>();

  function notifyChangedRows(nowSelectedEntryIds: Set<string>) {
    for (const entryId of nowSelectedEntryIds) {
      if (!previouslySelectedEntryIds.has(entryId)) {
        deps.notifyRowSelected(entryId, true);
      }
    }

    for (const entryId of previouslySelectedEntryIds) {
      if (!nowSelectedEntryIds.has(entryId)) {
        deps.notifyRowSelected(entryId, false);
      }
    }

    previouslySelectedEntryIds = nowSelectedEntryIds;
  }

  return {
    deltaTypes: ['selectionReplaced', 'selectionModified'],

    onDelta(_delta, state) {
      const visibleEntryIds = deps.databaseAdapter.getVisibleEntryIds();
      const getTotalEntryCount = () => visibleEntryIds.length;
      const resolveRowIndexToEntryId = (i: number) => visibleEntryIds[i];

      if (state.mode !== 'row' || state.selections.length === 0) {
        if (previouslySelectedEntryIds.size > 0) {
          for (const entryId of previouslySelectedEntryIds) {
            deps.notifyRowSelected(entryId, false);
          }

          previouslySelectedEntryIds = new Set();
        }

        deps.notifyHeaderCheckboxState(
          getHeaderCheckboxState(state, getTotalEntryCount())
        );

        return;
      }

      const nowSelectedEntryIds = new Set<string>();

      for (const range of state.selections) {
        const rowBounds = getRangeMinMax(range, 'row');

        for (let rowIndex = rowBounds.min; rowIndex <= rowBounds.max; rowIndex++) {
          const entryId = resolveRowIndexToEntryId(rowIndex);

          if (entryId) {
            nowSelectedEntryIds.add(entryId);
          }
        }
      }

      notifyChangedRows(nowSelectedEntryIds);

      deps.notifyHeaderCheckboxState(getHeaderCheckboxState(state, getTotalEntryCount()));
    }
  };
});
